/**
 * AI Agent Block Types
 *
 * Shared types for [[PLAN]] and [[ASK]] blocks used by the AI agent.
 * Plan blocks propose workflow changes; Ask blocks request structured user input.
 * Widget categories control how ask fields are rendered and displayed.
 */

import type { AskWidgetType } from "./ask_capabilities.generated";

// Re-export for convenience
export type { AskWidgetType };

// ── Plan Block Types ─────────────────────────────────────────────────

export interface PlanChange {
  action: "add" | "update" | "remove";
  nodeId: string;
  type?: string;
  description: string;
}

export interface PlanBlock {
  changes: PlanChange[];
  note?: string;
}

export interface ParsedPlanMessage {
  beforePlan: string;
  planBlock: PlanBlock | null;
  afterPlan: string;
}

// ── Ask Block Types ──────────────────────────────────────────────────

export interface AskOption {
  value: string;
  label: string;
  description?: string;
}

export interface AskQuestion {
  id: string;
  type: AskWidgetType;
  label: string;
  options?: AskOption[];
  allowOther?: boolean;
  otherPlaceholder?: string;
  placeholder?: string;
  description?: string;
  required?: boolean;
  default?: string | string[] | number | boolean | Record<string, unknown>;
  properties?: Record<string, any>;
  /** Chain family hint for wallet/address filtering ("evm" or "solana") */
  chainFamily?: "evm" | "solana";
}

export interface AskBlock {
  questions: AskQuestion[];
}

export interface ParsedMessage {
  beforeAsk: string;
  askBlock: AskBlock | null;
  afterAsk: string;
}

export type AskAnswers = Record<string, any>;

// ── Widget Categories ────────────────────────────────────────────────
// Used by AskBlockRenderer, CompletedAskDisplay, and FormField routing.
// When adding a new AskWidgetType, add it to the appropriate set here.

/** Basic input types handled inline (select, text, number) */
export const BASIC_WIDGET_TYPES = new Set<AskWidgetType>(["select", "multiselect", "text", "number"]);

/** Types that render as array values (multi-select chips) */
export const ARRAY_WIDGET_TYPES = new Set<AskWidgetType>(["chain-ids", "networks", "token-addresses"]);

/** Chain/network selector types — display with chain icon + name */
export const CHAIN_WIDGET_TYPES = new Set<AskWidgetType>(["chain-id", "chain-ids", "network", "networks"]);

/** Token selector types — display with token icon + symbol */
export const TOKEN_WIDGET_TYPES = new Set<AskWidgetType>(["token-address", "token-addresses", "asset-selector"]);

/** Address types — display with truncated monospace address */
export const ADDRESS_WIDGET_TYPES = new Set<AskWidgetType>([
  "recipient-address",
  "address",
  "contract-address",
  "turnkey-wallet",
  "morpho-vault",
]);

/** Date/time types — display with formatted date */
export const DATETIME_WIDGET_TYPES = new Set<AskWidgetType>(["date-time", "date"]);

/** Widget types that auto-submit the ASK form after their action (e.g. CREATE TABLE) */
export const SELF_SUBMIT_WIDGET_TYPES = new Set<AskWidgetType>(["database-table"]);

/** Price condition operator labels (shared between FormFieldConnector and CompletedAskDisplay) */
export const PRICE_CONDITION_LABELS: Record<string, string> = {
  ">": "Rises above",
  "<": "Falls below",
  ">=": "Rises to or above",
  "<=": "Falls to or below",
  "==": "Equals",
  "cross-up": "Crosses above",
  "cross-down": "Crosses below",
};

/** Trigger frequency labels for price alert maxTriggers field */
export const TRIGGER_FREQUENCY_LABELS: Record<string, string> = {
  once: "Once",
  "every-time": "Every time",
};

/** Backend sentinel for "unlimited runs". Display as empty/unlimited in the UI. */
export const UNLIMITED_MAX_RUNS = 99999;
